import { isApiError, type AdminSelf, type ApiClient, type QueryValue } from '@saay/api-client';
import { useApiClient } from '@saay/web-app';
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';

import type {
  AdminAuditEvent,
  AdminBuyer,
  AdminBuyersSummary,
  AdminBuyerSummary,
  AdminDashboard,
  AdminMerchant,
  AdminMerchantsSummary,
  AdminMerchantSummary,
  ApprovalRequest,
  ApprovalRequestSummary,
  BranchRequest,
  CapacityAdjustment,
  CommercialSettingVersion,
  GeographyArea,
  InstallmentPlanActivation,
  ListFilters,
  ListQuery,
  MerchantBranch,
  MerchantCategory,
  MerchantUserAssignment,
  LoginLockClearance,
  Page,
  PayoutAccount,
  PayoutAccountChangeRequest,
  PlatformRoleAssignmentListItem,
  PlatformRoleAssignmentsSummary,
  AdminBuyerWallet,
  AdminMoneyTimelineItem,
  AdminPayment,
  AdminPaymentDetail,
  AdminWalletSummary,
  AdminWalletTopUp,
  AdminWalletTransaction,
  AdminWalletTransfer,
  AdminWalletWithdrawal,
  DelinquencyCase,
  DelinquencyCaseSummary,
  WriteOffCandidate,
  AdminReturnRequest,
  AdminReturnRequestSummary,
  PolicyType,
  PolicyVersion,
  PhysicalCardCityUsage,
  PhysicalCardNumberingUsage,
  PhysicalCardPrintBatch,
  PhysicalCardPrintReady,
  AdminCreditProgramsSummary,
  AdminOrganizationsSummary,
  BuyerRestriction,
  CreditProgram,
  EnrollmentRequest,
  EnrollmentRequestMember,
  Organization,
  OrganizationMembership,
  OrganizationRole,
  OrganizationSummary,
  TargetingRun,
  TargetingRunDetail,
  CashSettlementBatch,
  CashSettlementBatchItem,
  SettlementAttempt,
  SettlementBatch,
  SettlementBatchItem,
  SettlementHold,
  MerchantRecoveryEntry,
  ReconciliationItem,
  BusinessCalendarDay,
  CalendarSyncRun,
  CalendarSyncRunDetail,
} from './api.js';
import type {
  AdminMarketingSummary,
  CommissionPayoutAttempt,
  CommissionPayoutHold,
  MarketingAssignment,
  MarketingAssignmentSummary,
  MarketingCommissionLine,
  MarketingRecipient,
  MarketingStructure,
  MarketingStructureSummary,
  MarketerCommissionSummary,
} from './marketing-types.js';

export const adminSelfQueryKey = ['admin', 'me'] as const;

/** `GET /admin/me` (`getAdminMe`, AC §17.4.1). */
export function useAdminSelf() {
  const client = useApiClient();
  return useQuery({
    queryFn: async ({ signal }) => (await client.get<AdminSelf>('/admin/me', { signal })).data,
    queryKey: adminSelfQueryKey,
  });
}

/** The page size of every setup list (AC §4.8 default). */
export const PAGE_SIZE = 25;

type Query = Readonly<Record<string, QueryValue>>;

/** Drops empty filter values so they are not sent (and do not change the cursor binding). */
export function compactQuery(query: Query): Query {
  return Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== ''),
  );
}

/** Reads every page of a short reference list (geography, categories) with the cursor. */
async function fetchAll<T>(
  client: ApiClient,
  path: string,
  query: Query,
  signal: AbortSignal,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (;;) {
    const { data } = await client.get<Page<T>>(path, {
      query: { ...query, limit: 100, ...(cursor === undefined ? {} : { cursor }) },
      signal,
    });
    items.push(...data.items);
    if (!data.page.hasMore || data.page.nextCursor === null) {
      return items;
    }
    cursor = data.page.nextCursor;
  }
}

interface CursorListOptions<T = unknown> {
  readonly enabled?: boolean;
  /**
   * Asks the first page for the capped exact total (`includeTotal=true`, AC §18.19.3). Later pages
   * never send it (it is ignored with a cursor); the screen keeps the first page's total.
   */
  readonly includeTotal?: boolean;
  /** Refetch every `pollMs` while this holds for the loaded items (D-59: an `EXECUTING` run). */
  readonly pollWhile?: (items: readonly T[]) => boolean;
  readonly pollMs?: number;
}

/** A cursor-paginated list (AC §4.8): «بیشتر» loads the next page with `nextCursor`. */
function useCursorList<T>(
  key: readonly unknown[],
  path: string,
  query: Query,
  { enabled = true, includeTotal = false, pollMs, pollWhile }: CursorListOptions<T> = {},
) {
  const client = useApiClient();
  const filters = compactQuery(query);
  return useInfiniteQuery({
    enabled,
    refetchInterval: (state) =>
      pollWhile !== undefined &&
      pollMs !== undefined &&
      pollWhile(state.state.data?.pages.flatMap((page) => page.items) ?? [])
        ? pollMs
        : false,
    getNextPageParam: (last: Page<T>) =>
      last.page.hasMore && last.page.nextCursor !== null ? last.page.nextCursor : undefined,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      (
        await client.get<Page<T>>(path, {
          query: {
            ...filters,
            limit: PAGE_SIZE,
            ...(pageParam === undefined
              ? includeTotal
                ? { includeTotal: 'true' }
                : {}
              : { cursor: pageParam }),
          },
          signal,
        })
      ).data,
    queryKey: [...key, filters],
  });
}

/** The first page's `page.total` of a list asked with `includeTotal` (AC §18.19.3). */
export function firstPageTotal(data: { readonly pages: readonly Page<unknown>[] } | undefined) {
  return data?.pages[0]?.page.total;
}

/** A failed «بیشتر» keeps the loaded rows on screen (the error is shown under the table). */
export function listStatus(query: {
  readonly isFetchNextPageError: boolean;
  readonly status: 'pending' | 'error' | 'success';
}): 'pending' | 'error' | 'success' {
  return query.isFetchNextPageError ? 'success' : query.status;
}

export const setupKeys = {
  approval: (id: string) => ['admin', 'approval-requests', id] as const,
  approvals: ['admin', 'approval-requests'] as const,
  audit: ['admin', 'audit-events'] as const,
  buyer: (id: string) => ['admin', 'buyers', id] as const,
  buyerTimeline: (id: string) => ['admin', 'buyers', id, 'money-timeline'] as const,
  buyerWallet: (id: string) => ['admin', 'buyers', id, 'wallet'] as const,
  buyers: ['admin', 'buyers'] as const,
  categories: ['admin', 'merchant-categories'] as const,
  cities: (provinceId: string) => ['admin', 'geography', 'cities', provinceId] as const,
  merchant: (id: string) => ['admin', 'merchants', id] as const,
  merchants: ['admin', 'merchants'] as const,
  delinquencyCase: (id: string) => ['admin', 'delinquency-cases', id] as const,
  delinquencyCases: ['admin', 'delinquency-cases'] as const,
  payment: (id: string) => ['admin', 'payments', id] as const,
  payments: ['admin', 'payments'] as const,
  writeOffCandidates: ['admin', 'write-off-candidates'] as const,
  policy: (type: PolicyType) => ['admin', 'policies', type] as const,
  returnRequest: (id: string) => ['admin', 'return-requests', id] as const,
  returnRequests: ['admin', 'return-requests'] as const,
  provinces: ['admin', 'geography', 'provinces'] as const,
  roleAssignments: ['admin', 'platform-role-assignments'] as const,
  summaries: ['admin', 'summaries'] as const,
  organization: (id: string) => ['admin', 'organizations', id] as const,
  organizations: ['admin', 'organizations'] as const,
  organizationRoles: ['admin', 'organization-roles'] as const,
  enrollment: (id: string) => ['admin', 'enrollment-requests', id] as const,
  program: (id: string) => ['admin', 'credit-programs', id] as const,
  programs: ['admin', 'credit-programs'] as const,
  settlementBatches: ['admin', 'settlement-batches'] as const,
  cashSettlementBatches: ['admin', 'cash-settlement-batches'] as const,
  settlementAttempts: ['admin', 'settlement-attempts'] as const,
  settlementHolds: ['admin', 'settlement-holds'] as const,
  recoveryEntries: ['admin', 'merchant-recovery-entries'] as const,
  reconciliation: ['admin', 'reconciliation-items'] as const,
  calendarDays: ['admin', 'business-calendar', 'days'] as const,
  calendarRuns: ['admin', 'business-calendar', 'sync-runs'] as const,
  calendarRun: (id: string) => ['admin', 'business-calendar', 'sync-runs', id] as const,
  marketingSummary: ['admin', 'marketing', 'summary'] as const,
  marketerSummary: ['admin', 'marketing', 'me', 'summary'] as const,
  marketingRecipients: ['admin', 'marketing-recipients'] as const,
  marketingRecipient: (id: string) => ['admin', 'marketing-recipients', id] as const,
  marketingStructures: ['admin', 'marketing-structures'] as const,
  marketingStructure: (id: string) => ['admin', 'marketing-structures', id] as const,
  marketingAssignments: ['admin', 'marketing-assignments'] as const,
  marketingAssignment: (id: string) => ['admin', 'marketing-assignments', id] as const,
  marketingCommissions: ['admin', 'marketing-commissions'] as const,
  commissionPayoutAttempts: ['admin', 'commission-payout-attempts'] as const,
  commissionPayoutAttempt: (id: string) => ['admin', 'commission-payout-attempts', id] as const,
  commissionPayoutHolds: ['admin', 'commission-payout-holds'] as const,
  walletSummary: ['admin', 'wallets', 'summary'] as const,
  walletTopUps: ['admin', 'wallet-top-ups'] as const,
  walletTransactions: ['admin', 'wallet-transactions'] as const,
  walletTransfers: ['admin', 'wallet-transfers'] as const,
  walletWithdrawals: ['admin', 'wallet-withdrawals'] as const,
  numbering: ['admin', 'physical-card-numbering'] as const,
  numberingUsage: ['admin', 'physical-card-numbering', 'usage'] as const,
  numberingCityUsage: ['admin', 'physical-card-numbering', 'city-usage'] as const,
};

// ---------------------------------------------------------------------------------------------
// Geography and categories (AC §17.5.1)

export function useProvinces() {
  const client = useApiClient();
  return useQuery({
    queryFn: ({ signal }) =>
      fetchAll<GeographyArea>(client, '/admin/geography/provinces', {}, signal),
    queryKey: setupKeys.provinces,
  });
}

export function useCities(provinceId: string | undefined) {
  const client = useApiClient();
  return useQuery({
    enabled: provinceId !== undefined && provinceId !== '',
    queryFn: ({ signal }) =>
      fetchAll<GeographyArea>(
        client,
        `/admin/geography/provinces/${encodeURIComponent(provinceId ?? '')}/cities`,
        {},
        signal,
      ),
    queryKey: setupKeys.cities(provinceId ?? ''),
  });
}

export function useCategories(status?: 'ACTIVE' | 'INACTIVE') {
  const client = useApiClient();
  const query: ListQuery<'listMerchantCategories'> = status === undefined ? {} : { status };
  return useQuery({
    queryFn: ({ signal }) =>
      fetchAll<MerchantCategory>(client, '/admin/merchant-categories', query, signal),
    queryKey: [...setupKeys.categories, query],
  });
}

// ---------------------------------------------------------------------------------------------
// Merchants (AC §17.5.2, §17.5.4)

/** `listAdminMerchants` filters (AC §17.5.2), from the generated query. */
export type MerchantFilters = ListFilters<'listAdminMerchants'>;

export function useMerchants(filters: MerchantFilters, enabled = true) {
  return useCursorList<AdminMerchantSummary>(
    setupKeys.merchants,
    '/admin/merchants',
    { ...filters },
    { enabled, includeTotal: true },
  );
}

function merchantPath(id: string, tail = ''): string {
  return `/admin/merchants/${encodeURIComponent(id)}${tail}`;
}

export function useMerchant(id: string) {
  const client = useApiClient();
  return useQuery({
    queryFn: async ({ signal }) =>
      (await client.get<AdminMerchant>(merchantPath(id), { signal })).data,
    queryKey: setupKeys.merchant(id),
  });
}

export function useMerchantBranches(id: string) {
  return useCursorList<MerchantBranch>(
    [...setupKeys.merchant(id), 'branches'],
    merchantPath(id, '/branches'),
    {},
  );
}

export function useMerchantUsers(id: string) {
  return useCursorList<MerchantUserAssignment>(
    [...setupKeys.merchant(id), 'users'],
    merchantPath(id, '/users'),
    {},
  );
}

export function usePlanActivations(id: string) {
  return useCursorList<InstallmentPlanActivation>(
    [...setupKeys.merchant(id), 'plans'],
    merchantPath(id, '/installment-plan-activations'),
    {},
  );
}

// ---------------------------------------------------------------------------------------------
// Buyers (AC §17.5.5)

/** `listAdminBuyers` filters (AC §17.5.5), from the generated query. */
export type BuyerFilters = ListFilters<'listAdminBuyers'>;

export function useBuyers(filters: BuyerFilters, enabled = true) {
  return useCursorList<AdminBuyerSummary>(
    setupKeys.buyers,
    '/admin/buyers',
    { ...filters },
    { enabled, includeTotal: true },
  );
}

export function useBuyer(id: string) {
  const client = useApiClient();
  return useQuery({
    queryFn: async ({ signal }) =>
      (await client.get<AdminBuyer>(`/admin/buyers/${encodeURIComponent(id)}`, { signal })).data,
    queryKey: setupKeys.buyer(id),
  });
}

// ---------------------------------------------------------------------------------------------
// Policies (AC §17.5.3)

export type WalletTopUpFilters = ListFilters<'listAdminWalletTopUps'>;
export type WalletTransactionFilters = ListFilters<'listAdminWalletTransactions'>;
export type WalletTransferFilters = ListFilters<'listAdminWalletTransfers'>;
export type WalletWithdrawalFilters = ListFilters<'listAdminWalletWithdrawals'>;
export type MoneyTimelineFilters = ListFilters<'listAdminBuyerMoneyTimeline'>;

export function useAdminWalletSummary(enabled = true) {
  const client = useApiClient();
  return useQuery({
    enabled,
    queryFn: async ({ signal }) =>
      (await client.get<AdminWalletSummary>('/admin/wallets/summary', { signal })).data,
    queryKey: setupKeys.walletSummary,
  });
}

export function useAdminWalletTopUps(filters: WalletTopUpFilters, enabled = true) {
  return useCursorList<AdminWalletTopUp>(
    setupKeys.walletTopUps,
    '/admin/wallet-top-ups',
    { ...filters },
    { enabled },
  );
}

export function useAdminWalletTransactions(filters: WalletTransactionFilters, enabled = true) {
  return useCursorList<AdminWalletTransaction>(
    setupKeys.walletTransactions,
    '/admin/wallet-transactions',
    { ...filters },
    { enabled },
  );
}

/** WL28: buyer, date, and amount filters, newest first or largest first. */
export function useAdminWalletTransfers(filters: WalletTransferFilters, enabled = true) {
  return useCursorList<AdminWalletTransfer>(
    setupKeys.walletTransfers,
    '/admin/wallet-transfers',
    { ...filters },
    { enabled },
  );
}

export function useAdminWalletWithdrawals(filters: WalletWithdrawalFilters, enabled = true) {
  return useCursorList<AdminWalletWithdrawal>(
    setupKeys.walletWithdrawals,
    '/admin/wallet-withdrawals',
    { ...filters },
    { enabled },
  );
}

export function useAdminBuyerWallet(buyerId: string, enabled = true) {
  const client = useApiClient();
  return useQuery({
    enabled: enabled && buyerId !== '',
    queryFn: async ({ signal }) =>
      (
        await client.get<AdminBuyerWallet>(`/admin/buyers/${encodeURIComponent(buyerId)}/wallet`, {
          signal,
        })
      ).data,
    queryKey: setupKeys.buyerWallet(buyerId),
  });
}

export function useAdminBuyerMoneyTimeline(
  buyerId: string,
  filters: MoneyTimelineFilters,
  enabled = true,
) {
  return useCursorList<AdminMoneyTimelineItem>(
    setupKeys.buyerTimeline(buyerId),
    `/admin/buyers/${encodeURIComponent(buyerId)}/money-timeline`,
    { ...filters },
    { enabled: enabled && buyerId !== '' },
  );
}

export function useCurrentPolicy<T extends PolicyVersion = PolicyVersion>(
  type: PolicyType,
  enabled = true,
) {
  const client = useApiClient();
  return useQuery({
    enabled,
    queryFn: async ({ signal }) =>
      (await client.get<T>(`/admin/policies/${type}/current`, { signal })).data,
    queryKey: [...setupKeys.policy(type), 'current'],
  });
}

export type PaymentFilters = ListFilters<'listAdminPayments'>;
export type ReturnRequestFilters = ListFilters<'listAdminReturnRequests'>;

export function useAdminPayments(filters: PaymentFilters, enabled = true) {
  return useCursorList<AdminPayment>(
    setupKeys.payments,
    '/admin/payments',
    { ...filters },
    {
      enabled,
    },
  );
}

export type DelinquencyCaseFilters = ListFilters<'listDelinquencyCases'>;

export function useDelinquencyCases(filters: DelinquencyCaseFilters, enabled = true) {
  return useCursorList<DelinquencyCaseSummary>(
    setupKeys.delinquencyCases,
    '/admin/delinquency-cases',
    { ...filters },
    { enabled },
  );
}

export function useDelinquencyCase(id: string) {
  const client = useApiClient();
  return useQuery({
    enabled: id !== '',
    queryFn: async ({ signal }) =>
      (
        await client.get<DelinquencyCase>(`/admin/delinquency-cases/${encodeURIComponent(id)}`, {
          signal,
        })
      ).data,
    queryKey: setupKeys.delinquencyCase(id),
  });
}

export type WriteOffCandidateFilters = ListFilters<'listWriteOffCandidates'>;

export function useWriteOffCandidates(filters: WriteOffCandidateFilters, enabled = true) {
  return useCursorList<WriteOffCandidate>(
    setupKeys.writeOffCandidates,
    '/admin/write-off-candidates',
    { ...filters },
    { enabled },
  );
}

export function useAdminPayment(id: string) {
  const client = useApiClient();
  return useQuery({
    enabled: id !== '',
    queryFn: async ({ signal }) =>
      (
        await client.get<AdminPaymentDetail>(`/admin/payments/${encodeURIComponent(id)}`, {
          signal,
        })
      ).data,
    queryKey: setupKeys.payment(id),
  });
}

/** Open return requests for the «مرجوعی‌های باز» inbox (RT7, AC §24.10.7). */
export const OPEN_RETURN_STATUSES = 'REQUESTED,WAITING_FOR_BUYER';

export function useAdminReturnRequests(filters: ReturnRequestFilters, enabled = true) {
  return useCursorList<AdminReturnRequestSummary>(
    setupKeys.returnRequests,
    '/admin/return-requests',
    { ...filters },
    { enabled },
  );
}

export function useAdminReturnRequest(id: string) {
  const client = useApiClient();
  return useQuery({
    enabled: id !== '',
    queryFn: async ({ signal }) =>
      (
        await client.get<AdminReturnRequest>(`/admin/return-requests/${encodeURIComponent(id)}`, {
          signal,
        })
      ).data,
    queryKey: setupKeys.returnRequest(id),
  });
}

export function usePolicyVersions(type: PolicyType, enabled = true) {
  return useCursorList<PolicyVersion>(
    [...setupKeys.policy(type), 'versions'],
    `/admin/policies/${type}/versions`,
    {},
    { enabled },
  );
}

// ---------------------------------------------------------------------------------------------
// Approval requests (AC §18.2, A1–A2)

/** `listApprovalRequests` filters (AC §18.2.7), from the generated query. */
export type ApprovalFilters = ListFilters<'listApprovalRequests'>;

export function useApprovals(filters: ApprovalFilters) {
  return useCursorList<ApprovalRequestSummary>(setupKeys.approvals, '/admin/approval-requests', {
    ...filters,
  });
}

export function approvalPath(id: string, tail = ''): string {
  return `/admin/approval-requests/${encodeURIComponent(id)}${tail}`;
}

export function useApproval(id: string) {
  const client = useApiClient();
  return useQuery({
    queryFn: async ({ signal }) =>
      (await client.get<ApprovalRequest>(approvalPath(id), { signal })).data,
    queryKey: setupKeys.approval(id),
  });
}

// ---------------------------------------------------------------------------------------------
// Merchant capacity history (AC §18.4.8, M11)

export function useCapacityAdjustments(merchantId: string) {
  return useCursorList<CapacityAdjustment>(
    [...setupKeys.merchant(merchantId), 'capacity-adjustments'],
    merchantPath(merchantId, '/capacity-adjustments'),
    {},
  );
}

// ---------------------------------------------------------------------------------------------
// Platform role assignments and audit (AC §18.12, R1, R3)

/** `listPlatformRoleAssignments` filters (AC §18.12), from the generated query. */
export type RoleAssignmentFilters = ListFilters<'listPlatformRoleAssignments'>;

export function useRoleAssignments(filters: RoleAssignmentFilters, enabled = true) {
  return useCursorList<PlatformRoleAssignmentListItem>(
    setupKeys.roleAssignments,
    '/admin/platform-role-assignments',
    { ...filters },
    { enabled },
  );
}

/** `listAdminAuditEvents` filters (AC §11.15, §18.12), from the generated query. */
export type AuditFilters = ListFilters<'listAdminAuditEvents'>;

/** R3 pages by keyset (`occurred_at DESC, id DESC`): the opaque cursor carries the last row. */
export function useAuditEvents(filters: AuditFilters) {
  return useCursorList<AdminAuditEvent>(setupKeys.audit, '/admin/audit-events', { ...filters });
}

/** One installment line for a `FINANCIAL_ADJUSTMENT` (ADM-30); from the lookup API when present. */
export type LedgerInstallmentTarget = {
  readonly balanceIrr: number;
  readonly buyerId: string;
  readonly creditAllocationId: string;
  readonly installmentId: string;
  readonly saleId: string;
  readonly saleReference: string;
  readonly sequence: number;
};

export function useLedgerInstallmentTargets(saleReference: string) {
  const client = useApiClient();
  const query = saleReference.trim();
  return useQuery({
    enabled: query.length >= 4,
    queryFn: async ({ signal }) => {
      try {
        const { data } = await client.get<{ items: LedgerInstallmentTarget[] }>(
          '/admin/financial-adjustment-targets',
          { query: { saleReference: query }, signal },
        );
        return data.items;
      } catch (error) {
        if (isApiError(error) && (error.status === 404 || error.status === 501)) {
          return [];
        }
        throw error;
      }
    },
    queryKey: ['admin', 'financial-adjustment-targets', query],
  });
}

// ---------------------------------------------------------------------------------------------
// D-48 summaries (AC §18.19.6). The server computes every figure; screens only format them.

/** Polls no more often than `max(maxStalenessSeconds, 30)` seconds (AC §18.19.2 rule 3). */
function summaryPollMs(data: { readonly maxStalenessSeconds: number } | undefined): number {
  return Math.max(data?.maxStalenessSeconds ?? 60, 30) * 1000;
}

function useSummary<T extends { readonly maxStalenessSeconds: number }>(
  name: string,
  path: string,
  { enabled = true, poll = false }: { readonly enabled?: boolean; readonly poll?: boolean } = {},
) {
  const client = useApiClient();
  return useQuery<T>({
    enabled,
    queryFn: async ({ signal }) => (await client.get<T>(path, { signal })).data,
    queryKey: [...setupKeys.summaries, name],
    refetchInterval: poll ? (query) => summaryPollMs(query.state.data) : false,
  });
}

/** S4 `getAdminDashboard`; `confirmations` and `approvals` are live, so the screen polls. */
export function useAdminDashboard(enabled = true) {
  return useSummary<AdminDashboard>('dashboard', '/admin/dashboard', { enabled, poll: true });
}

/** S5 `getAdminMerchantsSummary`. */
export function useMerchantsSummary() {
  return useSummary<AdminMerchantsSummary>('merchants', '/admin/merchants/summary');
}

/** S6 `getAdminBuyersSummary` (no Marketing Admin, D-31). */
export function useBuyersSummary(enabled = true) {
  return useSummary<AdminBuyersSummary>('buyers', '/admin/buyers/summary', { enabled });
}

/** S9 `getPlatformRoleAssignmentsSummary` (Super Admin only). */
export function useRoleAssignmentsSummary(enabled = true) {
  return useSummary<PlatformRoleAssignmentsSummary>(
    'platform-role-assignments',
    '/admin/platform-role-assignments/summary',
    { enabled },
  );
}

// ---------------------------------------------------------------------------------------------
// Slice 2: merchant lifecycle (AC §18.4) and payout accounts (§18.5)

export function useCommercialSettingVersions(merchantId: string) {
  return useCursorList<CommercialSettingVersion>(
    [...setupKeys.merchant(merchantId), 'commercial-setting-versions'],
    merchantPath(merchantId, '/commercial-setting-versions'),
    {},
  );
}

/** `listAdminBranchRequests` (M5) for one merchant (`merchantId` filter), newest first. */
export function useBranchRequests(merchantId: string, status?: string) {
  return useCursorList<BranchRequest>(
    [...setupKeys.merchant(merchantId), 'branch-requests'],
    '/admin/branch-requests',
    { merchantId, ...(status === undefined ? {} : { status }) },
  );
}

/** `listPayoutAccounts` (P4) of one merchant owner. */
export function usePayoutAccounts(merchantId: string, enabled = true) {
  return useCursorList<PayoutAccount>(
    [...setupKeys.merchant(merchantId), 'payout-accounts'],
    '/admin/payout-accounts',
    { ownerId: merchantId, ownerType: 'MERCHANT' },
    { enabled },
  );
}

/** `listPayoutAccountChangeRequests` (P8) of one merchant. */
export function usePayoutChangeRequests(merchantId: string, enabled = true) {
  return useCursorList<PayoutAccountChangeRequest>(
    [...setupKeys.merchant(merchantId), 'payout-change-requests'],
    '/admin/payout-account-change-requests',
    { merchantId },
    { enabled },
  );
}

// ---------------------------------------------------------------------------------------------
// Merchant requests inbox (owner requirement: every merchant-originated request on «درخواست‌ها»)

/**
 * The inbox lists sit under `setupKeys.merchants`, so the merchant page's refresh after a decision
 * (`useRefreshMerchant` invalidates `setupKeys.merchants`) also refreshes the inbox and its count.
 */
export const merchantInboxKeys = {
  branchRequests: [...setupKeys.merchants, 'inbox', 'branch-requests'] as const,
  payoutChangeRequests: [...setupKeys.merchants, 'inbox', 'payout-change-requests'] as const,
};

/** `listAdminBranchRequests` (M5) of every merchant, newest first; `status` is a CSV filter. */
export function useInboxBranchRequests(status: string | undefined, enabled = true) {
  return useCursorList<BranchRequest>(
    merchantInboxKeys.branchRequests,
    '/admin/branch-requests',
    status === undefined ? {} : { status },
    { enabled },
  );
}

/** `listPayoutAccountChangeRequests` (P8) of every merchant, newest first; `status` is a CSV filter. */
export function useInboxPayoutChangeRequests(status: string | undefined, enabled = true) {
  return useCursorList<PayoutAccountChangeRequest>(
    merchantInboxKeys.payoutChangeRequests,
    '/admin/payout-account-change-requests',
    status === undefined ? {} : { status },
    { enabled },
  );
}

// ---------------------------------------------------------------------------------------------
// Login-lock clearance (AC §18.21 L2): polled every 2 s for up to 30 s while REQUESTED.

export const CLEARANCE_POLL_MS = 2_000;
export const CLEARANCE_POLL_LIMIT_MS = 30_000;

export function clearancePath(userId: string, clearanceId?: string): string {
  const base = `/admin/identity-users/${encodeURIComponent(userId)}/login-lock-clearances`;
  return clearanceId === undefined ? base : `${base}/${encodeURIComponent(clearanceId)}`;
}

export function useLoginLockClearance(
  userId: string,
  clearanceId: string | undefined,
  startedAt: number,
  now: () => number = Date.now,
) {
  const client = useApiClient();
  return useQuery({
    enabled: clearanceId !== undefined,
    queryFn: async ({ signal }) =>
      (await client.get<LoginLockClearance>(clearancePath(userId, clearanceId), { signal })).data,
    queryKey: ['admin', 'login-lock-clearances', userId, clearanceId ?? ''],
    refetchInterval: (query) =>
      query.state.data?.status === 'REQUESTED' && now() - startedAt < CLEARANCE_POLL_LIMIT_MS
        ? CLEARANCE_POLL_MS
        : false,
  });
}

// ---------------------------------------------------------------------------------------------
// Slice 3: buyers (AC §18.6), organizations and enrollment (§18.8), credit programs (§18.9)

/** `listBuyerRestrictions` (B5), newest first. */
export function useBuyerRestrictions(buyerId: string, enabled = true) {
  return useCursorList<BuyerRestriction>(
    [...setupKeys.buyer(buyerId), 'restrictions'],
    `/admin/buyers/${encodeURIComponent(buyerId)}/restrictions`,
    {},
    { enabled },
  );
}

/** `listOrganizations` filters (O1), from the generated query. */
export type OrganizationFilters = ListFilters<'listOrganizations'>;

export function useOrganizations(filters: OrganizationFilters, enabled = true) {
  return useCursorList<OrganizationSummary>(
    setupKeys.organizations,
    '/admin/organizations',
    { ...filters },
    { enabled },
  );
}

/** Every ACTIVE organization (a picker for programs, targeting, and memberships). */
export function useActiveOrganizations(enabled = true) {
  const client = useApiClient();
  return useQuery({
    enabled,
    queryFn: ({ signal }) =>
      fetchAll<OrganizationSummary>(
        client,
        '/admin/organizations',
        { sort: 'displayNameFa', status: 'ACTIVE' },
        signal,
      ),
    queryKey: [...setupKeys.organizations, 'active-all'],
  });
}

export function organizationPath(id: string, tail = ''): string {
  return `/admin/organizations/${encodeURIComponent(id)}${tail}`;
}

export function useOrganization(id: string) {
  const client = useApiClient();
  return useQuery({
    queryFn: async ({ signal }) =>
      (await client.get<Organization>(organizationPath(id), { signal })).data,
    queryKey: setupKeys.organization(id),
  });
}

/** `listOrganizationMemberships` filters (O5). */
export type MembershipFilters = ListFilters<'listOrganizationMemberships'>;

export function useOrganizationMemberships(id: string, filters: MembershipFilters = {}) {
  return useCursorList<OrganizationMembership>(
    [...setupKeys.organization(id), 'memberships'],
    organizationPath(id, '/memberships'),
    { ...filters },
  );
}

/** `listOrganizationRoles` filters (O8). */
export type OrganizationRoleFilters = ListFilters<'listOrganizationRoles'>;

export function useOrganizationRoles(filters: OrganizationRoleFilters = {}, enabled = true) {
  return useCursorList<OrganizationRole>(
    setupKeys.organizationRoles,
    '/admin/organization-roles',
    { ...filters },
    { enabled },
  );
}

/** Every ACTIVE catalog role (pickers). */
export function useActiveOrganizationRoles(enabled = true) {
  const client = useApiClient();
  return useQuery({
    enabled,
    queryFn: ({ signal }) =>
      fetchAll<OrganizationRole>(client, '/admin/organization-roles', { status: 'ACTIVE' }, signal),
    queryKey: [...setupKeys.organizationRoles, 'active-all'],
  });
}

export function useEnrollmentRequests(organizationId: string) {
  return useCursorList<EnrollmentRequest>(
    [...setupKeys.organization(organizationId), 'enrollment-requests'],
    organizationPath(organizationId, '/enrollment-requests'),
    {},
  );
}

export function enrollmentPath(id: string, tail = ''): string {
  return `/admin/organization-enrollment-requests/${encodeURIComponent(id)}${tail}`;
}

/** O12; polled every 3 s while the request is `PROVISIONING` (the job commits row by row). */
export function useEnrollmentRequest(id: string) {
  const client = useApiClient();
  return useQuery({
    queryFn: async ({ signal }) =>
      (await client.get<EnrollmentRequest>(enrollmentPath(id), { signal })).data,
    queryKey: setupKeys.enrollment(id),
    refetchInterval: (query) => (query.state.data?.status === 'PROVISIONING' ? 3_000 : false),
  });
}

/** `listEnrollmentRequestMembers` filters (O13). */
export type EnrollmentMemberFilters = ListFilters<'listEnrollmentRequestMembers'>;

export function useEnrollmentMembers(
  id: string,
  filters: EnrollmentMemberFilters = {},
  enabled = true,
) {
  return useCursorList<EnrollmentRequestMember>(
    [...setupKeys.enrollment(id), 'members'],
    enrollmentPath(id, '/members'),
    { ...filters },
    { enabled },
  );
}

/** `listCreditPrograms` filters (C1). */
export type CreditProgramFilters = ListFilters<'listCreditPrograms'>;

export function useCreditPrograms(filters: CreditProgramFilters, enabled = true) {
  return useCursorList<CreditProgram>(
    setupKeys.programs,
    '/admin/credit-programs',
    { ...filters },
    { enabled },
  );
}

export function programPath(id: string, tail = ''): string {
  return `/admin/credit-programs/${encodeURIComponent(id)}${tail}`;
}

export function useCreditProgram(id: string, enabled = true) {
  const client = useApiClient();
  return useQuery({
    enabled,
    queryFn: async ({ signal }) =>
      (await client.get<CreditProgram>(programPath(id), { signal })).data,
    queryKey: setupKeys.program(id),
  });
}

/** `listCreditProgramTargetingRuns` filters (C9). */
export type TargetingRunFilters = ListFilters<'listCreditProgramTargetingRuns'>;

/** C9 and C10 poll while a run is EXECUTING (D-59, AC §18.9.2). */
export const TARGETING_RUN_POLL_MS = 3_000;

/**
 * C9. The list refreshes itself while one of its runs is `EXECUTING`, so a run started on another
 * device or before a reload still shows its progress and its end (the state lives on the server).
 */
export function useTargetingRuns(programId: string, filters: TargetingRunFilters = {}) {
  return useCursorList<TargetingRun>(
    [...setupKeys.program(programId), 'runs'],
    programPath(programId, '/targeting-runs'),
    { ...filters },
    {
      pollMs: TARGETING_RUN_POLL_MS,
      pollWhile: (items) => items.some((run) => run.status === 'EXECUTING'),
    },
  );
}

/**
 * C10 with its members paged by `membersCursor` (limit 100): the run comes once; «بیشتر» asks the next
 * members page of the same run.
 */
export function useTargetingRun(programId: string, runId: string) {
  const client = useApiClient();
  return useInfiniteQuery({
    getNextPageParam: (last: TargetingRunDetail) =>
      last.members.page.hasMore && last.members.page.nextCursor !== null
        ? last.members.page.nextCursor
        : undefined,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      (
        await client.get<TargetingRunDetail>(
          programPath(programId, `/targeting-runs/${encodeURIComponent(runId)}`),
          {
            query: {
              membersLimit: 100,
              ...(pageParam === undefined ? {} : { membersCursor: pageParam }),
            },
            signal,
          },
        )
      ).data,
    queryKey: [...setupKeys.program(programId), 'runs', runId],
    refetchInterval: (query) => {
      const status = query.state.data?.pages[0]?.status;
      return status === 'EXECUTING' ? TARGETING_RUN_POLL_MS : false;
    },
  });
}

/** S7 `getAdminOrganizationsSummary`. */
export function useOrganizationsSummary(enabled = true) {
  return useSummary<AdminOrganizationsSummary>('organizations', '/admin/organizations/summary', {
    enabled,
  });
}

/** S8 `getAdminCreditProgramsSummary` (no Marketing Admin, D-31). */
export function useCreditProgramsSummary(enabled = true) {
  return useSummary<AdminCreditProgramsSummary>(
    'credit-programs',
    '/admin/credit-programs/summary',
    { enabled },
  );
}

// Stage 5 reads (AC §26). The server remains the source for all totals and calendar decisions.
export function useSettlementBatches() {
  return useCursorList<SettlementBatchItem>(
    setupKeys.settlementBatches,
    '/admin/settlement-batches',
    {},
  );
}

export function useSettlementBatch(id: string) {
  const client = useApiClient();
  return useQuery({
    enabled: id !== '',
    queryFn: async ({ signal }) =>
      (
        await client.get<SettlementBatch>(`/admin/settlement-batches/${encodeURIComponent(id)}`, {
          signal,
        })
      ).data,
    queryKey: [...setupKeys.settlementBatches, id],
  });
}

/** `listAdminSettlementAttempts`: no `batchKind` means both kinds (AC §34.7.6). */
export function useSettlementAttempts(status?: string, batchKind?: string) {
  return useCursorList<SettlementAttempt>(
    setupKeys.settlementAttempts,
    '/admin/settlement-attempts',
    {
      ...(status === undefined ? {} : { status }),
      ...(batchKind === undefined ? {} : { batchKind }),
    },
  );
}

/** `listAdminCashSettlementBatches` (CS3, AC §34.7.3). */
export function useCashSettlementBatches(filters: {
  readonly settlementDateFrom: string;
  readonly settlementDateTo: string;
  readonly status: string;
}) {
  return useCursorList<CashSettlementBatchItem>(
    setupKeys.cashSettlementBatches,
    '/admin/cash-settlement-batches',
    filters,
  );
}

/** `getAdminCashSettlementBatch` (CS4, AC §34.7.3). */
export function useCashSettlementBatch(id: string) {
  const client = useApiClient();
  return useQuery({
    enabled: id !== '',
    queryFn: async ({ signal }) =>
      (
        await client.get<CashSettlementBatch>(
          `/admin/cash-settlement-batches/${encodeURIComponent(id)}`,
          { signal },
        )
      ).data,
    queryKey: [...setupKeys.cashSettlementBatches, 'detail', id],
  });
}

export function useSettlementHolds() {
  return useCursorList<SettlementHold>(setupKeys.settlementHolds, '/admin/settlement-holds', {
    status: 'ACTIVE',
  });
}

export function useRecoveryEntries() {
  return useCursorList<MerchantRecoveryEntry>(
    setupKeys.recoveryEntries,
    '/admin/merchant-recovery-entries',
    {},
  );
}

export function useReconciliationItems() {
  return useCursorList<ReconciliationItem>(
    setupKeys.reconciliation,
    '/admin/reconciliation-items',
    {},
  );
}

export function useBusinessCalendarDays(dateFrom: string, dateTo: string) {
  const client = useApiClient();
  return useQuery({
    queryFn: async ({ signal }) =>
      (
        await client.get<{
          readonly coverage: { readonly populatedThrough: string | null };
          readonly enforced?: boolean;
          readonly days: BusinessCalendarDay[];
        }>('/admin/business-calendar/days', {
          query: { businessDateFrom: dateFrom, businessDateTo: dateTo },
          signal,
        })
      ).data,
    queryKey: [...setupKeys.calendarDays, dateFrom, dateTo],
  });
}

// ---------------------------------------------------------------------------------------------
// Stage 6 marketing (AC §28). Types live in marketing-types.ts until the generated client has them.

export interface MarketingListFilters {
  readonly batchId?: string;
  readonly commissionPayoutAttemptId?: string;
  readonly holdType?: string;
  readonly marketingRecipientId?: string;
  readonly merchantId?: string;
  readonly organizationId?: string;
  readonly q?: string;
  readonly scopeType?: string;
  readonly status?: string;
}

function marketingQuery(filters: MarketingListFilters): Query {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== ''),
  );
}

function useMarketingGet<T>(key: readonly unknown[], path: string, enabled = true) {
  const client = useApiClient();
  return useQuery({
    enabled,
    queryFn: async ({ signal }) => (await client.get<T>(path, { signal })).data,
    queryKey: key,
  });
}

export function useAdminMarketingSummary(enabled = true) {
  return useMarketingGet<AdminMarketingSummary>(
    setupKeys.marketingSummary,
    '/admin/marketing/summary',
    enabled,
  );
}

export function useMarketerCommissionSummary(enabled = true) {
  return useMarketingGet<MarketerCommissionSummary>(
    setupKeys.marketerSummary,
    '/admin/marketing/me/summary',
    enabled,
  );
}

export function useMarketingRecipients(filters: MarketingListFilters = {}, enabled = true) {
  return useCursorList<MarketingRecipient>(
    setupKeys.marketingRecipients,
    '/admin/marketing-recipients',
    marketingQuery(filters),
    { enabled },
  );
}

export function useMarketingRecipient(id: string) {
  return useMarketingGet<MarketingRecipient>(
    setupKeys.marketingRecipient(id),
    `/admin/marketing-recipients/${encodeURIComponent(id)}`,
    id !== '',
  );
}

export function useMarketingStructures(filters: MarketingListFilters = {}, enabled = true) {
  return useCursorList<MarketingStructureSummary>(
    setupKeys.marketingStructures,
    '/admin/marketing-structures',
    marketingQuery(filters),
    { enabled },
  );
}

export function useMarketingStructure(id: string, filters: MarketingListFilters = {}) {
  const client = useApiClient();
  return useQuery({
    enabled: id !== '',
    queryFn: async ({ signal }) =>
      (
        await client.get<MarketingStructure>(
          `/admin/marketing-structures/${encodeURIComponent(id)}`,
          { query: marketingQuery(filters), signal },
        )
      ).data,
    queryKey: [...setupKeys.marketingStructure(id), filters],
  });
}

export function useMarketingAssignments(filters: MarketingListFilters = {}, enabled = true) {
  return useCursorList<MarketingAssignmentSummary>(
    setupKeys.marketingAssignments,
    '/admin/marketing-assignments',
    marketingQuery(filters),
    { enabled },
  );
}

export function useMarketingAssignment(id: string) {
  return useMarketingGet<MarketingAssignment>(
    setupKeys.marketingAssignment(id),
    `/admin/marketing-assignments/${encodeURIComponent(id)}`,
    id !== '',
  );
}

export function useMarketingCommissions(filters: MarketingListFilters = {}, enabled = true) {
  return useCursorList<MarketingCommissionLine>(
    setupKeys.marketingCommissions,
    '/admin/marketing-commissions',
    marketingQuery(filters),
    { enabled },
  );
}

export function useCommissionPayoutAttempts(filters: MarketingListFilters = {}, enabled = true) {
  return useCursorList<CommissionPayoutAttempt>(
    setupKeys.commissionPayoutAttempts,
    '/admin/commission-payout-attempts',
    marketingQuery(filters),
    { enabled },
  );
}

export function useCommissionPayoutAttempt(id: string) {
  return useMarketingGet<CommissionPayoutAttempt>(
    setupKeys.commissionPayoutAttempt(id),
    `/admin/commission-payout-attempts/${encodeURIComponent(id)}`,
    id !== '',
  );
}

export function useCommissionPayoutHolds(filters: MarketingListFilters = {}, enabled = true) {
  return useCursorList<CommissionPayoutHold>(
    setupKeys.commissionPayoutHolds,
    '/admin/commission-payout-holds',
    marketingQuery(filters),
    { enabled },
  );
}

export function pageRows<T>(
  data: { readonly pages: readonly { readonly items: readonly T[] }[] } | undefined,
): T[] {
  return data?.pages.flatMap((item) => [...item.items]) ?? [];
}

export function useCalendarSyncRuns() {
  const client = useApiClient();
  return useQuery({
    queryFn: async ({ signal }) =>
      (
        await client.get<{ readonly items: CalendarSyncRun[] }>(
          '/admin/business-calendar/sync-runs',
          { signal },
        )
      ).data,
    queryKey: setupKeys.calendarRuns,
  });
}

export function useCalendarSyncRun(id: string) {
  const client = useApiClient();
  return useQuery({
    enabled: id !== '',
    queryFn: async ({ signal }) =>
      (
        await client.get<CalendarSyncRunDetail>(
          `/admin/business-calendar/sync-runs/${encodeURIComponent(id)}`,
          { signal },
        )
      ).data,
    queryKey: setupKeys.calendarRun(id),
  });
}

// ---------------------------------------------------------------------------------------------
// Physical-card numbering usage (D-117, AC §31.11.3 K8, §31.11.4 K9): counts only, no-store.

export function useNumberingUsage(enabled = true) {
  const client = useApiClient();
  return useQuery({
    enabled,
    queryFn: async ({ signal }) =>
      (
        await client.get<PhysicalCardNumberingUsage>('/admin/physical-card-numbering/usage', {
          signal,
        })
      ).data,
    queryKey: setupKeys.numberingUsage,
  });
}

// ---------------------------------------------------------------------------------------------
// Print batches (D-118, AC §32): P1 counts only (no-store), P3 metadata only. Nothing is kept
// between screens: the counts refresh on every visit and after each create or download.

export const printKeys = {
  all: ['admin', 'physical-card-print'] as const,
  batches: ['admin', 'physical-card-print', 'batches'] as const,
  ready: (cityIds: readonly string[]) =>
    ['admin', 'physical-card-print', 'ready', [...cityIds].sort()] as const,
};

/** P1 `getAdminPhysicalCardPrintReady`, optionally narrowed to some cities. */
export function usePrintReady(cityIds: readonly string[] = [], enabled = true) {
  const client = useApiClient();
  return useQuery({
    enabled,
    gcTime: 0,
    queryFn: async ({ signal }) =>
      (
        await client.get<PhysicalCardPrintReady>('/admin/physical-card-print-batches/ready', {
          query: cityIds.length === 0 ? {} : { cityId: [...cityIds].sort() },
          signal,
        })
      ).data,
    queryKey: printKeys.ready(cityIds),
    refetchOnMount: 'always',
  });
}

/** P3 `listAdminPhysicalCardPrintBatches`: newest first, «بیشتر» by `nextCursor`. */
export function usePrintBatches() {
  return useCursorList<PhysicalCardPrintBatch>(
    printKeys.batches,
    '/admin/physical-card-print-batches',
    {},
  );
}

export type CityUsageFilters = ListFilters<'listAdminPhysicalCardCityUsage'>;

export function useNumberingCityUsage(filters: CityUsageFilters, enabled = true) {
  return useCursorList<PhysicalCardCityUsage>(
    setupKeys.numberingCityUsage,
    '/admin/physical-card-numbering/city-usage',
    { ...filters },
    { enabled },
  );
}

/**
 * Every K9 row (up to 1,000 cities) for the given server filters: the editor reads which city codes
 * are locked, and the «شهرها» tab searches the city names in the whole list.
 */
export function useAllNumberingCities(
  filters: { readonly capState?: string; readonly provinceId?: string } = {},
  enabled = true,
) {
  const client = useApiClient();
  return useQuery({
    enabled,
    queryFn: ({ signal }) =>
      fetchAll<PhysicalCardCityUsage>(
        client,
        '/admin/physical-card-numbering/city-usage',
        { ...filters },
        signal,
      ),
    queryKey: [...setupKeys.numberingCityUsage, 'all', filters],
  });
}

/** The cities of several provinces at once (the numbering editor picks cities per province). */
export function useCitiesOf(provinceIds: readonly string[]) {
  const client = useApiClient();
  return useQueries({
    queries: provinceIds.map((provinceId) => ({
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchAll<GeographyArea>(
          client,
          `/admin/geography/provinces/${encodeURIComponent(provinceId)}/cities`,
          {},
          signal,
        ),
      queryKey: setupKeys.cities(provinceId),
    })),
  });
}
